/**
 * BuildInfo.js
 *
 * Componente que muestra información del build/deploy en el Admin Panel.
 * Permite identificar qué rama y commit está desplegado en cada ambiente.
 *
 * Formato: "AMBIENTE | rama | commit | fecha"
 * Ejemplo: "UAT | feature/vet-patients | a1b2c3d | 2025-12-05 14:32"
 */
import React, { useState, useEffect } from 'react';

// Intentar importar build-info.json generado en pre-build
let staticBuildInfo = null;
try {
  staticBuildInfo = require('../../build-info.json');
} catch (e) {
  // El archivo puede no existir en desarrollo
}

function BuildInfo({ position = 'footer' }) {
  const [buildInfo, setBuildInfo] = useState(staticBuildInfo);

  useEffect(() => {
    // Si no hay build-info estático, intentar cargarlo dinámicamente
    if (!buildInfo) {
      fetch('/build-info.json')
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setBuildInfo(data);
        })
        .catch(() => {
          // Usar valores de variables de entorno de React como fallback
          const envInfo = {
            environment: getEnvironmentFromEnv(),
            branch: process.env.REACT_APP_GIT_BRANCH || 'local',
            commitHash: process.env.REACT_APP_GIT_COMMIT?.substring(0, 7) || 'dev',
            buildDateFormatted: process.env.REACT_APP_BUILD_DATE || 'N/A'
          };
          setBuildInfo(envInfo);
        });
    }
  }, [buildInfo]);

  // Determinar ambiente desde variables de entorno
  function getEnvironmentFromEnv() {
    if (process.env.REACT_APP_ENV === 'production') return 'PROD';
    if (process.env.REACT_APP_ENV === 'testing') return 'UAT';
    if (process.env.NODE_ENV === 'production') return 'PROD';
    return 'DEV';
  }

  if (!buildInfo) {
    return null;
  }

  const { environment, branch, commitHash, buildDateFormatted } = buildInfo;

  // Colores por ambiente
  const envColors = {
    PROD: { bg: '#dc3545', text: '#fff' },      // Rojo - Producción
    UAT: { bg: '#fd7e14', text: '#fff' },       // Naranja - Testing
    DEV: { bg: '#6c757d', text: '#fff' }        // Gris - Desarrollo
  };

  const envStyle = envColors[environment] || envColors.DEV;

  // Truncar rama si es muy larga
  const displayBranch = branch.length > 25 ? branch.substring(0, 22) + '...' : branch;

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '6px 12px',
    fontSize: '11px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', monospace",
    color: '#6c757d',
    backgroundColor: position === 'fixed' ? 'rgba(255,255,255,0.95)' : 'transparent',
    borderTop: position === 'footer' ? '1px solid #dee2e6' : 'none',
    ...(position === 'fixed' && {
      position: 'fixed',
      bottom: '8px',
      right: '8px',
      borderRadius: '6px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      zIndex: 1000
    })
  };

  const badgeStyle = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    backgroundColor: envStyle.bg,
    color: envStyle.text
  };

  const separatorStyle = {
    color: '#adb5bd',
    margin: '0 2px'
  };

  const monoStyle = {
    fontFamily: 'monospace',
    fontWeight: 500,
    color: '#495057'
  };

  return (
    <div style={containerStyle} title={`Build: ${branch} @ ${commitHash}`}>
      <span style={badgeStyle}>{environment}</span>
      <span style={separatorStyle}>|</span>
      <span style={monoStyle} title={branch}>{displayBranch}</span>
      <span style={separatorStyle}>|</span>
      <span style={monoStyle}>{commitHash}</span>
      <span style={separatorStyle}>|</span>
      <span style={{ color: '#868e96' }}>{buildDateFormatted}</span>
    </div>
  );
}

export default BuildInfo;
